import { useEffect, useState } from "react";
import { Smartphone, QrCode, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";

export default function Payment() {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    api.get("/payment-info").then((r) => setInfo(r.data)).catch(() => {});
  }, []);

  const upiId = info?.upi_id || "";
  const payee = info?.payee_name || "Dr. Vrinda Jayaram";

  // We render a QR using a public QR image API only if a UPI id is set.
  // Otherwise we show a warm message that the QR will be shared after the call.
  const upiUri = upiId ? `upi://pay?pa=${encodeURIComponent(upiId)}&pn=${encodeURIComponent(payee)}` : null;
  const qrUrl = upiUri
    ? `https://api.qrserver.com/v1/create-qr-code/?size=260x260&data=${encodeURIComponent(upiUri)}`
    : null;

  return (
    <section id="payment" className="relative py-20 lg:py-28" data-testid="payment-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.2em] text-sunlit-mute mb-4">A note on payment</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl text-sunlit-ink leading-tight">
              Pay only after we <span className="italic text-sunlit-gold">meet first</span>.
            </h2>
            <div className="mt-6 space-y-4 text-base sm:text-lg text-sunlit-mute leading-relaxed max-w-2xl">
              <p>
                I do not collect any money before our complimentary 15-minute clarity call. If — after we speak — we both feel this is the right path for you, I'll share my <strong className="text-sunlit-ink">GPay / UPI</strong> QR over WhatsApp for the chosen service.
              </p>
              <p>
                No card forms. No subscriptions. Just one simple, transparent transfer.
              </p>
            </div>

            <ul className="mt-8 grid sm:grid-cols-3 gap-4">
              <Feature icon={Smartphone} title="GPay & UPI" sub="Any UPI app works" />
              <Feature icon={QrCode} title="Scan & pay" sub="QR shared after our call" />
              <Feature icon={ShieldCheck} title="No upfront fees" sub="Clarity call is always free" />
            </ul>
          </div>

          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-3 bg-sunlit-gold/15 rounded-[2rem] blur-2xl" />
              <div
                className="relative rounded-3xl bg-sunlit-cream border border-stone-200 p-8 shadow-sm"
                data-testid="payment-card"
              >
                <p className="text-xs uppercase tracking-widest text-sunlit-mute">After clarity call</p>
                <p className="font-serif-display text-2xl text-sunlit-ink mt-1">Your GPay QR</p>

                <div className="mt-6 mx-auto h-[260px] w-[260px] rounded-2xl bg-sunlit-ivory border border-stone-200/70 flex items-center justify-center overflow-hidden">
                  {qrUrl ? (
                    <img src={qrUrl} alt={`GPay QR for ${payee}`} className="h-full w-full object-contain p-3" data-testid="payment-qr-image" />
                  ) : (
                    <div className="text-center px-6" data-testid="payment-qr-placeholder">
                      <QrCode className="mx-auto h-10 w-10 text-sunlit-gold mb-3" />
                      <p className="font-serif-display text-lg text-sunlit-ink leading-snug">
                        Your QR will be shared on WhatsApp after our clarity call.
                      </p>
                    </div>
                  )}
                </div>

                <div className="mt-5 text-center">
                  <p className="text-sm text-sunlit-mute">Payee</p>
                  <p className="font-medium text-sunlit-ink">{payee}</p>
                  {upiId && (
                    <p className="mt-1 text-xs text-sunlit-mute font-mono" data-testid="payment-upi-id">{upiId}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Feature({ icon: Icon, title, sub }) {
  return (
    <li className="rounded-2xl bg-sunlit-cream border border-stone-200/70 p-4">
      <div className="h-8 w-8 rounded-full bg-sunlit-gold/20 flex items-center justify-center mb-2">
        <Icon className="h-4 w-4 text-sunlit-gold" />
      </div>
      <p className="font-medium text-sunlit-ink text-sm">{title}</p>
      <p className="text-xs text-sunlit-mute mt-0.5">{sub}</p>
    </li>
  );
}
